import { supabase } from './supabase';

export interface AIUsageDetails {
  usageCount: number;
  usageLimit: number;
  isWithinLimit: boolean;
  remainingUsage: number;
}

/**
 * Získá aktuální stav využití AI pro přihlášeného uživatele
 */
export async function getAIUsage(): Promise<AIUsageDetails | null> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.log('getAIUsage: User error or no user found', userError);
      throw userError || new Error('User not found');
    }
    
    console.log('getAIUsage: Fetching AI usage for user', userData.user.id);
    
    // First, ensure the user has an AI usage record for today
    await supabase.rpc('initialize_ai_usage', {
      user_uuid: userData.user.id
    });
    
    // Then get the usage data
    const { data: rawData, error } = await supabase.rpc('get_ai_usage', {
      user_uuid: userData.user.id
    });
    // get_ai_usage returns a JSON object; the generated types only know it as Json
    const data = rawData as { usage_count?: number; usage_limit?: number; is_within_limit?: boolean } | null;
    
    if (error) {
      console.error('getAIUsage: RPC error', error);
      throw error;
    }
    
    console.log('getAIUsage: Raw response data', data);
    
    // As a fallback, try to get the data directly from the table
    if (!data || data.usage_count === undefined) {
      console.log('getAIUsage: Falling back to direct table query');
      
      const { data: directData, error: directError } = await supabase
        .from('ai_usage')
        .select('usage_count')
        .eq('user_id', userData.user.id)
        .eq('usage_date', new Date().toISOString().split('T')[0])
        .single();
      
      if (directError) {
        console.error('getAIUsage: Direct query error', directError);
      } else if (directData) {
        console.log('getAIUsage: Direct query data', directData);
        
        // Use the direct data with default values for other fields
        return {
          usageCount: directData.usage_count || 0,
          usageLimit: 10, // Default to free tier limit
          isWithinLimit: (directData.usage_count || 0) < 10,
          remainingUsage: 10 - (directData.usage_count || 0)
        };
      }
    }
    
    if (data) {
      const result = {
        usageCount: typeof data.usage_count === 'number' ? data.usage_count : 0,
        usageLimit: typeof data.usage_limit === 'number' ? data.usage_limit : 10,
        isWithinLimit: data.is_within_limit === true,
        remainingUsage: typeof data.usage_limit === 'number' && typeof data.usage_count === 'number' 
          ? data.usage_limit - data.usage_count 
          : 10
      };
      
      console.log('getAIUsage: Processed result', result);
      return result;
    }
    
    console.log('getAIUsage: No data returned, using defaults');
    return {
      usageCount: 0,
      usageLimit: 10,
      isWithinLimit: true,
      remainingUsage: 10
    };
  } catch (error) {
    console.error('Error fetching AI usage:', error);
    // Return default values in case of error
    return {
      usageCount: 0,
      usageLimit: 10,
      isWithinLimit: true,
      remainingUsage: 10
    };
  }
}

/**
 * Zvýší počítadlo využití AI pro přihlášeného uživatele
 * Vrací true, pokud byl limit navýšen, false pokud byl limit překročen
 */
export async function incrementAIUsage(): Promise<boolean> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.log('incrementAIUsage: User error or no user found', userError);
      throw userError || new Error('User not found');
    }
    
    console.log('incrementAIUsage: Incrementing AI usage for user', userData.user.id);
    
    const { data, error } = await supabase.rpc('increment_ai_usage', {
      user_uuid: userData.user.id
    });
    
    if (error) {
      console.error('incrementAIUsage: RPC error', error);
      throw error;
    }
    
    console.log('incrementAIUsage: Result', data);
    
    return !!data;
  } catch (error) {
    console.error('Error incrementing AI usage:', error);
    return false;
  }
}

/**
 * Kontroluje, zda je uživatel v rámci svého denního limitu pro AI
 */
export async function isWithinAILimit(): Promise<boolean> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.log('isWithinAILimit: User error or no user found', userError);
      throw userError || new Error('User not found');
    }
    
    console.log('isWithinAILimit: Checking limit for user', userData.user.id);
    
    const { data, error } = await supabase.rpc('is_within_ai_limit', {
      user_uuid: userData.user.id
    });
    
    if (error) {
      console.error('isWithinAILimit: RPC error', error);
      throw error;
    }
    
    console.log('isWithinAILimit: Result', data);
    
    return !!data;
  } catch (error) {
    console.error('Error checking AI usage limit:', error);
    return false;
  }
}